/**
 * speechService.ts — Single source of truth for all TTS in the child app.
 *
 * Every page that speaks imports { speak, cancelSpeech } from here.
 * This guarantees one consistent voice across lessons, writing, tutor, books
 * and assessment — the voice the child (or parent) chose in preferences.
 *
 * Voice quality priority:
 *   1. Microsoft Neural (Edge / Chrome on Windows / Android Edge) — best
 *   2. Google Neural (Chrome Android)                             — very good
 *   3. Apple Neural (Safari iOS / macOS)                         — very good
 *   4. Google standard (older Android Chrome)                    — decent
 *   5. Microsoft SAPI / legacy                                   — fallback
 *   6. First available browser voice                             — last resort
 */

import { loadPreferences } from './preferences';
import { isMicRecordingLive, msUntilMicReleased } from '@learnwithai/ui-shared';
import { SPEECH_CHUNK_MAX_LEN, SPEECH_VOLUME, SPEECH_EMPHASIS_VOLUME, isCloudVoiceId, ttsAudioKey, initAudioCache, audioCacheHas, audioCacheCount, getAudioBlob, putAudioBlob, fetchCloudAudio, fetchLocalAudio, voiceSelectionRank, isForeignAccent, pickNativeVoiceIndex } from '@learnwithai/ui-shared';

// Re-exported for the voice pinning tests (os-voice-en-us-only) — the picker itself
// is single-sourced in @learnwithai/ui-shared/voiceAccent (accent-impossible arch),
// shared byte-for-byte with web-child's speechService.
export { pickNativeVoiceIndex } from '@learnwithai/ui-shared';

// ── Global TTS playback volume ───────────────────────────────────────────────
// ONE consistent target for every speech category on every tier (prebaked /
// device-cache / cloud / neural / native plugin / OS utterance). Shared with
// web-child's speechService via lessonConstants.SPEECH_VOLUME — this service
// powers the arcade games (web AND mobile), which used to play at the default
// 1.0 while lessons played at 0.9: the tester-reported "voice differs in volume
// through the app". Guarded by speech-volume-consistency.test.ts.
function applyVolume(audio: HTMLAudioElement): void { audio.volume = SPEECH_VOLUME; }

// ── Slow sound-out emphasis (the WordCorrection second read) ─────────────────
// The prebaked / pack / device-cache / cloud <audio> clips IGNORE the speakAtRate
// `rate` param — it only reaches the OS-utterance fallback — so the "sound it out
// slower" reread used to play at NORMAL speed wherever the HD floor served it
// (the exact bug: slower reread worked on web-child but not on web-app lessons).
// speakEmphasizedAtRate sets this flag; every play tier below consumes it via
// applyEmphasis (playbackRate, pitch preserved, full volume). Normal speak() /
// speakAtRate() clear it so ordinary speech is never slowed. Mirrors web-child.
let _emphasis: { rate: number } | null = null;

function applyEmphasis(audio: HTMLAudioElement): void {
  if (!_emphasis) return;
  (audio as HTMLAudioElement & { preservesPitch?: boolean }).preservesPitch = true;
  audio.playbackRate = _emphasis.rate;    // < 1 → slower, natural pitch
  audio.volume = SPEECH_EMPHASIS_VOLUME;  // documented exception: full volume on the replay
}

export interface CachedVoice {
  voice: SpeechSynthesisVoice | null;
  rate: number;
  pitch: number;
}

export interface NativeVoice {
  name: string;
  lang: string;
  voiceURI?: string;
  localService?: boolean;
}

interface NativeTextToSpeechPlugin {
  speak: (options: {
    text: string;
    lang?: string;
    rate?: number;
    pitch?: number;
    volume?: number;
    voice?: number;
    category?: string;
    queueStrategy?: number;
  }) => Promise<void>;
  stop?: () => Promise<void>;
  getSupportedVoices?: () => Promise<{ voices: NativeVoice[] }>;
}

function getNativeTextToSpeech(): NativeTextToSpeechPlugin | null {
  return (window as unknown as {
    Capacitor?: { Plugins?: { TextToSpeech?: NativeTextToSpeechPlugin } };
  }).Capacitor?.Plugins?.TextToSpeech ?? null;
}

// ── Native (Capacitor) voice selection — ACCENT-IMPOSSIBLE (2026-08-01) ──────
// The native TTS plugin's speak() takes a `voice` INDEX into getSupportedVoices().
// Without it iOS/Android use the SYSTEM DEFAULT voice, which on some devices is a
// foreign accent (en-IN) — a US-phonics tutor must never read the arcade games in
// an Indian accent. The picker is single-sourced in ui-shared (pickNativeVoiceIndex,
// shared with web-child); this resolves the index FRESH for every utterance:
// H1 (index skew): the plugin dereferences the raw integer against a FRESH
// AVSpeechSynthesisVoice.speechVoices() at speak time, and iOS mutates/grows that
// list after cold start — a cached index can land on adjacent en-IN. NEVER cache
// the list or the index; enumerate right before speak and use that same snapshot.
// If enumeration throws or returns empty THIS utterance, the caller REFUSES —
// a stale index is never reused, and a voice-less speak never happens.
async function resolveNativeVoiceForUtterance(plugin: NativeTextToSpeechPlugin): Promise<number | null> {
  if (!plugin.getSupportedVoices) return null; // can't prove an acceptable voice → refuse
  try {
    const { voices } = await plugin.getSupportedVoices();
    if (!Array.isArray(voices) || voices.length === 0) return null;
    const voiceId = loadPreferences().voiceId ?? '';
    return pickNativeVoiceIndex(voices, _langCode, isCloudVoiceId(voiceId) ? '' : voiceId);
  } catch {
    return null;
  }
}

// ── OS-speech refusal tripwire (accent-impossible architecture) ──────────────
// Mirrors web-child's counter: when no acceptable-accent voice exists, the OS
// tier REFUSES (no TTS call, no onStart, immediate onEnd). Owner mandate
// 2026-08-01: silence over a foreign accent, always.
let _osRefusedCount = 0;

/** How many utterances the OS tier refused this session (no acceptable voice). */
export function getOsSpeechRefusedCount(): number { return _osRefusedCount; }

function refuseOsSpeech(reason: string): void {
  _osRefusedCount++;
  try {
    const w = globalThis as unknown as { __lwaOsRefused?: number };
    w.__lwaOsRefused = (w.__lwaOsRefused ?? 0) + 1;
  } catch { /* ignore */ }
  console.error(`[tts] CRITICAL: OS speech refused — no acceptable ${_langCode} voice; ${reason}. Silence over a foreign accent (owner mandate 2026-08-01); onEnd fired so pacing/progression continue.`);
}

/**
 * Returns true when running inside a Capacitor iOS or Android shell.
 * Web Speech API inside WKWebView / Android WebView is unreliable — it may
 * not fire `onend`, causing the listening countdown to never appear.
 * Prefer the native TTS plugin whenever it's available.
 */
function isCapacitorNative(): boolean {
  return !!(window as unknown as {
    Capacitor?: { isNativePlatform?: () => boolean };
  }).Capacitor?.isNativePlatform?.();
}

// ── Quality-ranked voice matcher ────────────────────────────────────────────

interface VoiceCandidate {
  match: RegExp;
  rate: number;
  pitch: number;
  priority: number; // lower = better
}

const QUALITY_RANKED: VoiceCandidate[] = [
  // ── Tier 1: Microsoft Neural Online (Edge / Chrome) ──
  { match: /Microsoft (Aria|Jenny|Sara|Emma|Ana|Ashley|Michelle|Eric|Guy|Davis|Andrew|Brian|Christopher|Ryan|Sonia|Eloise|Denise|Henri|Sylvie|Antoine|Jean) Online/i, rate: 0.91, pitch: 1.0, priority: 10 },
  // ── Tier 2: Google Neural (Android Chrome) ──
  { match: /^Google US English$/i,        rate: 0.91, pitch: 1.0, priority: 20 },
  { match: /^Google UK English Female$/i, rate: 0.91, pitch: 1.0, priority: 21 },
  { match: /^Google UK English Male$/i,   rate: 0.91, pitch: 1.0, priority: 22 },
  { match: /^Google français$/i,          rate: 0.91, pitch: 1.0, priority: 23 },
  // ── Tier 3: Apple Neural (iOS / macOS Safari) ──
  { match: /^Samantha( \(enhanced\))?$/i, rate: 0.89, pitch: 1.0, priority: 30 },
  { match: /^Karen( \(enhanced\))?$/i,    rate: 0.89, pitch: 1.0, priority: 31 },
  { match: /^Daniel( \(enhanced\))?$/i,   rate: 0.89, pitch: 1.0, priority: 32 },
  { match: /^Moira( \(enhanced\))?$/i,    rate: 0.89, pitch: 1.0, priority: 33 },
  { match: /^Tessa( \(enhanced\))?$/i,    rate: 0.89, pitch: 1.0, priority: 34 },
  { match: /^(Amélie|Thomas|Aurelie)( \(enhanced\))?$/i, rate: 0.89, pitch: 1.0, priority: 35 },
  // ── Tier 4: Apple compact (iOS baseline) ──
  { match: /^Samantha$/i,                 rate: 0.88, pitch: 1.0, priority: 40 },
  { match: /^(Karen|Daniel|Tom)$/i,       rate: 0.88, pitch: 1.0, priority: 41 },
  // ── Tier 5: Legacy Microsoft SAPI ──
  { match: /^Microsoft Zira/i,            rate: 0.85, pitch: 1.1, priority: 50 },
  { match: /^Microsoft David/i,           rate: 0.85, pitch: 1.0, priority: 51 },
  { match: /^Microsoft Hortense/i,        rate: 0.85, pitch: 1.0, priority: 52 },
];

/**
 * Legacy browser/OS voice preferences are disabled app-wide while the kid-facing
 * picker is hidden (mirrors web-child's kill switch — keep the two in sync).
 * Saved `browser:*` preferences stay stored; flipping this back to true restores them.
 */
export const LEGACY_BROWSER_VOICES_ENABLED = false;

/**
 * Pick the best available voice for the given language code.
 * If voiceId is supplied (saved preference) try to match that first.
 */
export function resolveBestVoice(voiceId: string, langCode: string): CachedVoice {
  const voices = window.speechSynthesis?.getVoices() ?? [];
  const langPrefix = langCode.slice(0, 2);
  const candidates = voices.filter(v => v.lang.startsWith(langPrefix));
  if (!LEGACY_BROWSER_VOICES_ENABLED && voiceId.startsWith('browser:')) voiceId = '';

  // 1. Try saved preference ID (strip browser: prefix, skip cloud voice IDs) — but
  //    never honour a foreign-accent saved voice (en-IN/en-ZA/… for an EN session):
  //    a US-phonics tutor must not read the arcade games in an Indian accent even
  //    when a stale preference names one.
  if (voiceId && !isCloudVoiceId(voiceId)) {
    const normalizedId = voiceId.startsWith('browser:') ? voiceId.slice('browser:'.length) : voiceId;
    const saved = candidates.find(v =>
      (v.name.replace(/\s+/g, '-').toLowerCase() === normalizedId ||
      v.name === normalizedId) && !isForeignAccent(v.lang, langCode)
    );
    if (saved) {
      const ranked = QUALITY_RANKED.find(r => r.match.test(saved.name));
      return { voice: saved, rate: ranked?.rate ?? 0.91, pitch: ranked?.pitch ?? 1.0 };
    }
  }

  // 2. Pick best available by ACCENT match first (en-US must beat en-IN etc. — a
  //    US-phonics tutor must never read in a foreign accent), then by voice-name
  //    quality. Foreign-accent candidates are SKIPPED outright — silence over a
  //    foreign accent (owner mandate 2026-08-01); the old last resort is gone.
  let best: { voice: SpeechSynthesisVoice; rate: number; pitch: number; rank: number } | null = null;
  for (const v of candidates) {
    if (isForeignAccent(v.lang, langCode)) continue; // never selectable, even as the only option
    const ranked = QUALITY_RANKED.find(r => r.match.test(v.name));
    const rank = voiceSelectionRank(v.lang, langCode, ranked ? ranked.priority : 60);
    if (!best || rank < best.rank) {
      best = { voice: v, rate: ranked?.rate ?? 0.90, pitch: ranked?.pitch ?? 1.0, rank };
    }
  }
  if (best) return { voice: best.voice, rate: best.rate, pitch: best.pitch };

  // 3. No ACCEPTABLE-accent voice at all → null (the OS tier then REFUSES the
  //    utterance — never voice-less, never a foreign accent).
  return { voice: null, rate: 0.90, pitch: 1.0 };
}

// ── Singleton voice cache ────────────────────────────────────────────────────

let _cache: CachedVoice | null = null;
let _langCode = 'en-US';

function ensureCache(): CachedVoice {
  if (_cache) return _cache;
  const prefs = loadPreferences();
  _cache = resolveBestVoice(prefs.voiceId ?? '', _langCode);
  return _cache;
}

/** Call this when language changes or preferences are saved. (The NATIVE voice
 *  index is never cached — it is resolved fresh per utterance, H1 — so only the
 *  browser-voice cache needs clearing here.) */
export function invalidateVoiceCache(langCode?: string): void {
  if (langCode) _langCode = langCode;
  _cache = null;
}

/** Keep cache in sync when voiceschanged fires (browser loads voices async). */
if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
  window.speechSynthesis.addEventListener('voiceschanged', () => { _cache = null; });
}

// ── Browser (WebView) voice readiness — P3 of the accent-impossible arch ─────
// Mirrors web-child: WebKit/Chrome populate getVoices() ASYNC, and speaking with
// NO voice + utter.lang lets the engine pick its own default — which can be a
// foreign accent. Wait ONCE per session for `voiceschanged` (bounded) before the
// first resolution; after that a voice-less utterance is REFUSED, never spoken.
const VOICES_READY_TIMEOUT_MS = 1500;
let _voicesWait: Promise<void> | null = null;
let _voicesWaitDone = false;

/** True once voices are known (non-empty) or the one-per-session wait finished. */
function browserVoicesSettled(): boolean {
  if (_voicesWaitDone) return true;
  try {
    if ((window.speechSynthesis?.getVoices?.() ?? []).length > 0) {
      _voicesWaitDone = true;
      return true;
    }
  } catch { /* fall through */ }
  return false;
}

/** Wait (once per session) for voiceschanged or the bounded timeout. */
function ensureBrowserVoicesReady(): Promise<void> {
  if (_voicesWait) return _voicesWait;
  _voicesWait = new Promise<void>((resolve) => {
    const finish = () => { _voicesWaitDone = true; resolve(); };
    try {
      const synth = window.speechSynthesis;
      let done = false;
      const once = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        try { synth.removeEventListener?.('voiceschanged', once); } catch { /* ignore */ }
        finish();
      };
      const timer = setTimeout(once, VOICES_READY_TIMEOUT_MS);
      try { synth.addEventListener?.('voiceschanged', once); } catch { /* the timeout covers it */ }
    } catch {
      finish();
    }
  });
  return _voicesWait;
}

// ── Text chunking (copied from lessonConstants pattern) ──────────────────────

export function chunkText(text: string, maxLen = SPEECH_CHUNK_MAX_LEN): string[] {
  if (text.length <= maxLen) return [text];
  const chunks: string[] = [];
  let remaining = text;
  while (remaining.length > maxLen) {
    let cut = remaining.lastIndexOf('. ', maxLen);
    if (cut < 80) cut = remaining.lastIndexOf('! ', maxLen);
    if (cut < 80) cut = remaining.lastIndexOf('? ', maxLen);
    if (cut < 80) cut = remaining.lastIndexOf(', ', maxLen);
    if (cut < 80) cut = remaining.lastIndexOf(' ', maxLen);
    if (cut < 1) cut = maxLen;
    else cut += 1;
    chunks.push(remaining.slice(0, cut).trim());
    remaining = remaining.slice(cut).trim();
  }
  if (remaining.length > 0) chunks.push(remaining);
  return chunks;
}

// ── Chrome keep-alive (prevents speechSynthesis from stalling after ~15s) ───

let _keepAliveTimer: ReturnType<typeof setInterval> | null = null;

function startKeepAlive(): void {
  stopKeepAlive();
  _keepAliveTimer = setInterval(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis?.speaking) {
      window.speechSynthesis.pause();
      window.speechSynthesis.resume();
    }
  }, 14000);
}

function stopKeepAlive(): void {
  if (_keepAliveTimer) { clearInterval(_keepAliveTimer); _keepAliveTimer = null; }
}

function speakNative(text: string, rate: number, onEnd?: () => void): void {
  // Belt-and-braces iOS mic crash guard: never start audio over a live record session.
  if (isCapacitorNative() && isMicRecordingLive()) { onEnd?.(); return; }
  const plugin = getNativeTextToSpeech();
  if (!plugin) { onEnd?.(); return; }

  const clean = text.replace(EMOJI_RE, '').trim();
  if (!clean) { onEnd?.(); return; }

  const chunks = chunkText(clean);
  void (async () => {
    try {
      await plugin.stop?.();
      // ACCENT-IMPOSSIBLE (H1+H2): resolve the voice index against a FRESH device
      // enumeration for THIS utterance, and REQUIRE an accent-acceptable voice.
      // null (no acceptable voice / empty or failed enumeration) → REFUSE: never
      // call plugin.speak voice-less (Android's engine default for the locale can
      // be en-IN) and never reuse a stale index (iOS reorders the voice list after
      // cold start). The finally below still fires onEnd, so callers never hang.
      const voice = await resolveNativeVoiceForUtterance(plugin);
      if (voice === null) {
        refuseOsSpeech('native voice enumeration was empty/failed or listed only foreign-accent voices');
        return;
      }
      for (const chunk of chunks) {
        await plugin.speak({
          text: chunk,
          lang: _langCode,
          rate,
          pitch: 1.0,
          volume: SPEECH_VOLUME,
          voice, // REQUIRED (H2) — a voice-less native speak is the accent hole
          category: 'playback',
          queueStrategy: 0,
        });
      }
    } finally {
      onEnd?.();
    }
  })();
}

// ── Core speak / cancel ──────────────────────────────────────────────────────

const EMOJI_RE = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu;

/**
 * Speak text using the best available voice.
 * Handles chunking for Chrome's ~200 char limit, chains via onend.
 * In Capacitor shells, prefers the native TTS plugin for reliable audio and
 * guaranteed onEnd callbacks (Web Speech API in WebViews silently drops onend).
 */
// ── Cloud (neural) TTS ───────────────────────────────────────────────────────
// Mirrors web-child/speechService's cloud layer so the arcade games speak with
// the same Azure HD voice as lessons when configured (mobile calls
// configureCloudTts). Disabled (null) → native/browser voice. Falls back to the
// local voice on any failure/offline.
// TODO: consolidate this duplicated cloud layer (web-app + web-child) into a
// shared module (e.g. @learnwithai/ui-shared) so it's maintained once.
interface CloudTtsConfig {
  endpoint: string;
  voice?: string;
  getToken?: () => string | null;
  // Ordered candidate bearer tokens; a 401/403 on one retries the next (app token
  // → live JWT) so a drifted app token can't 401 all HD audio (robotic fallback).
  getTokens?: () => Array<string | null>;
}

// Ordered auth candidates for a cloud-TTS request (see web-child speechService).
function cloudTokens(): Array<string | null> {
  const cfg = _cloudTts;
  if (!cfg) return [null];
  if (cfg.getTokens) {
    const list = cfg.getTokens();
    if (list && list.length) return list;
  }
  return [cfg.getToken?.() ?? null];
}
// An <audio> element that owns a blob object URL. `_revoke` frees it exactly
// once — called when playback ends, errors, fails to start, or is stopped — so
// no object URL is ever orphaned.
type TrackedAudio = HTMLAudioElement & { _revoke?: () => void };
let _cloudTts: CloudTtsConfig | null = null;
let _cloudAudio: TrackedAudio | null = null;
// Skip the cloud endpoint for a while after repeated failures so a dead backend
// (e.g. unreachable on the LAN) doesn't add latency before the on-device voice.
let _cloudFailures = 0;
let _cloudSkipUntil = 0;
// Azure HD needs room for longer lines; 3s timed out legit-slow responses and
// dropped games to the robot voice. Quick to retry so cloud isn't disabled for
// long stretches (the "in and out" robot effect).
const CLOUD_TIMEOUT_MS = 8000;
const CLOUD_FAIL_THRESHOLD = 3;
const CLOUD_BACKOFF_MS = 20000;

export function configureCloudTts(cfg: CloudTtsConfig | null): void {
  _cloudTts = cfg;
  _cloudFailures = 0;
  _cloudSkipUntil = 0;
  // Warm the shared on-device audio cache (same store as lessons) so previously
  // heard game audio plays HD/offline. Idempotent; no-ops without IndexedDB.
  void initAudioCache();
}

export function getCloudTtsConfig(): CloudTtsConfig | null { return _cloudTts; }

function cloudAvailable(): boolean {
  return !!_cloudTts && navigator.onLine && Date.now() >= _cloudSkipUntil;
}

function resolvedCloudVoice(): string {
  const { voiceId } = loadPreferences();
  const wantFr = _langCode.toLowerCase().startsWith('fr');
  // Only honour the saved cloud voice when it matches the CONTENT language —
  // otherwise French text gets spoken by an English voice (and vice versa).
  if (voiceId && isCloudVoiceId(voiceId) && voiceId.toLowerCase().startsWith('fr-') === wantFr) {
    return voiceId;
  }
  if (wantFr) return 'fr-FR-DeniseNeural';
  // Null-safe (mirrors web-child): callers may compute cache keys via this
  // function before cloud TTS is configured.
  return _cloudTts?.voice ?? 'en-US-Ava:DragonHDLatestNeural';
}

export function getEffectiveCloudVoice(defaultVoice: string): string {
  const { voiceId } = loadPreferences();
  if (voiceId && isCloudVoiceId(voiceId)) return voiceId;
  return defaultVoice;
}

async function playCloud(text: string, rate: number, onEnd?: () => void, seq?: number): Promise<boolean> {
  // Belt-and-braces iOS mic crash guard: never start audio over a live record session.
  if (isCapacitorNative() && isMicRecordingLive()) { onEnd?.(); return true; }
  if (!cloudAvailable()) return false;
  // cloudTextFor = emoji-strip + pronunciation fixes — the SAME transform the
  // device-cache lookup tier keys on. Synthesizing/storing the raw text while
  // looking up the fixed text meant the cache NEVER hit for any line the fixes
  // touch (most sentences: 'a' → 'uh'), so games re-fetched from the cloud on
  // every single play and dropped to the robotic OS voice whenever it failed.
  const clean = cloudTextFor(text);
  if (!clean) { onEnd?.(); return true; }
  const fail = (): boolean => {
    if (++_cloudFailures >= CLOUD_FAIL_THRESHOLD) {
      _cloudSkipUntil = Date.now() + CLOUD_BACKOFF_MS;
      console.warn(`[tts] cloud TTS hit ${CLOUD_FAIL_THRESHOLD} failures — falling back to on-device voice for ${CLOUD_BACKOFF_MS / 1000}s. Check /api/media/tts auth and Azure key.`);
    }
    return false;
  };
  try {
    const token = cloudTokens();
    // Binary-safe on native (CapacitorHttp corrupts a `fetch().blob()` MP3).
    const blob = await fetchCloudAudio(
      _cloudTts!.endpoint,
      token,
      { text: clean, voice: resolvedCloudVoice(), rate, locale: _langCode },
      CLOUD_TIMEOUT_MS,
    );
    if (!blob) return fail();
    // Persist for offline + cost: this exact line plays HD next time with no
    // network (shared cache, keyed identically to the device-cache lookup tier).
    void putAudioBlob(ttsAudioKey(clean, resolvedCloudVoice()), blob);
    // Superseded while the fetch was in flight — a newer utterance owns playback.
    if (seq !== undefined && seq !== _speakSeq) return true;
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url) as TrackedAudio;
    applyVolume(audio);
    applyEmphasis(audio); // slow sound-out → slower override
    let revoked = false;
    audio._revoke = () => { if (!revoked) { revoked = true; URL.revokeObjectURL(url); } };
    _cloudAudio = audio;
    const done = () => { audio._revoke?.(); if (_cloudAudio === audio) _cloudAudio = null; onEnd?.(); };
    audio.onended = done;
    audio.onerror = done;
    try {
      await audio.play();
    } catch (e: any) {
      audio._revoke?.();
      if (_cloudAudio === audio) _cloudAudio = null;
      // Chrome blocks audio.play() until the first user gesture (autoplay policy).
      // This is a browser policy, NOT a backend failure — do not increment the
      // failure counter or trigger the 20-second backoff that makes all subsequent
      // TTS use the robotic OS voice for the rest of the session.
      if (e?.name === 'NotAllowedError') return false;
      return fail();
    }
    _cloudFailures = 0; _cloudSkipUntil = 0;
    return true;
  } catch {
    return fail();
  }
}

// ── Offline neural TTS (on-device Piper / VITS via WASM) ──────────────────────
// Mirrors web-child's offline-neural tier so the arcade games speak with the same
// natural on-device voice as lessons. The voice model is shared (same OPFS origin)
// so it's downloaded once for the whole app. Falls back to the OS voice while the
// model is downloading or on any error. English-only for now (en_US model).
interface OfflineNeuralConfig { voiceId: string; }
let _neural: OfflineNeuralConfig | null = null;
let _neuralAudio: TrackedAudio | null = null;
let _neuralReady = false;
type PiperModule = typeof import('@mintplex-labs/piper-tts-web');
let _piperPromise: Promise<PiperModule | null> | null = null;

function loadPiper(): Promise<PiperModule | null> {
  if (!_piperPromise) {
    _piperPromise = import('@mintplex-labs/piper-tts-web')
      .catch((e) => { console.warn('[tts] piper module load failed', e); return null; });
  }
  return _piperPromise;
}

export function configureOfflineNeural(cfg: OfflineNeuralConfig | null): void {
  _neural = cfg;
  _neuralReady = false;
  if (!cfg) return;
  void (async () => {
    const piper = await loadPiper();
    if (!piper || _neural?.voiceId !== cfg.voiceId) return;
    try {
      await piper.download(cfg.voiceId);
      if (_neural?.voiceId === cfg.voiceId) _neuralReady = true;
    } catch (e) {
      console.warn('[tts] neural voice download failed', e);
    }
  })();
}

export function isOfflineNeuralReady(): boolean { return _neuralReady; }

function neuralAvailable(): boolean {
  return !!_neural && _neuralReady && _langCode.toLowerCase().startsWith('en');
}

async function playNeural(text: string, rate: number, onEnd?: () => void, seq?: number): Promise<boolean> {
  // Belt-and-braces iOS mic crash guard: never start audio over a live record session.
  if (isCapacitorNative() && isMicRecordingLive()) { onEnd?.(); return true; }
  if (!neuralAvailable()) return false;
  const clean = text.replace(EMOJI_RE, '').trim();
  if (!clean) { onEnd?.(); return true; }
  const piper = await loadPiper();
  if (!piper || !_neural) return false;
  try {
    const wav = await piper.predict({ text: clean, voiceId: _neural.voiceId });
    if (!(wav instanceof Blob) || !wav.size) return false;
    // Superseded while synthesising — a newer utterance owns playback.
    if (seq !== undefined && seq !== _speakSeq) return true;
    const url = URL.createObjectURL(wav);
    const audio = new Audio(url) as TrackedAudio;
    applyVolume(audio);
    let revoked = false;
    audio._revoke = () => { if (!revoked) { revoked = true; URL.revokeObjectURL(url); } };
    (audio as HTMLAudioElement & { preservesPitch?: boolean }).preservesPitch = true;
    audio.playbackRate = Math.min(1.3, Math.max(0.7, rate));
    applyEmphasis(audio); // slow sound-out → slower override
    _neuralAudio = audio;
    const done = () => { audio._revoke?.(); if (_neuralAudio === audio) _neuralAudio = null; onEnd?.(); };
    audio.onended = done;
    audio.onerror = done;
    try {
      await audio.play();
    } catch (e) {
      audio._revoke?.();
      if (_neuralAudio === audio) _neuralAudio = null;
      console.warn('[tts] neural playback blocked', e);
      return false;
    }
    return true;
  } catch (e) {
    console.warn('[tts] neural synth failed', e);
    return false;
  }
}

// ── Prebaked audio (pre-generated bundle — top tier, mirrors web-child) ──────
// Lets the arcade games play the same committed Azure HD bundle as lessons:
// consistent voice, offline, no auth. Falls through to cache/cloud/neural/OS.
let _prebakedBase: string | null = null;
let _prebakedVoice = '';
// FR lines key under the FRENCH voice (manifest.frVoice) first — mirrors
// web-child's FR-first lookup so French game words play the native-FR clips.
let _prebakedFrVoice = '';
let _prebakedFormat = 'mp3';
let _prebakedKeys: Set<string> | null = null;
let _prebakedAudio: TrackedAudio | null = null;

/** Shape of the pre-generated audio manifest (voice/format + all keyed lines). */
export interface PrebakedManifest { voice?: string; frVoice?: string; format?: string; keys?: string[] }

export function configurePrebakedAudio(cfg: { baseUrl: string; manifest?: PrebakedManifest; refresh?: boolean } | null): void {
  _prebakedKeys = null;
  _prebakedBase = cfg ? cfg.baseUrl.replace(/\/$/, '') : null;
  if (!_prebakedBase) return;

  const applyManifest = (m: PrebakedManifest): void => {
    _prebakedVoice = m.voice ?? '';
    _prebakedFrVoice = m.frVoice ?? '';
    _prebakedFormat = m.format ?? 'mp3';
    _prebakedKeys = new Set(m.keys ?? []);
  };

  // A manifest bundled into the JS at build time populates the floor SYNCHRONOUSLY.
  // On native this is the ROBUST path: a runtime manifest fetch routes through
  // Capacitor's patched fetch (CapacitorHttp) and can fail for a local same-origin
  // asset, silently disabling the offline HD floor so games' spoken words drop to the
  // robotic OS voice. On WEB the bundled manifest is the FALLBACK floor: the browser
  // fetch below refreshes to the full served manifest, but if it fails/races the
  // bundled keys keep the floor HD (never empty). Clip <audio> loads the local file
  // directly (no fetch), so bundling just the manifest is enough. (See web-child.)
  if (cfg?.manifest && Array.isArray(cfg.manifest.keys)) {
    applyManifest(cfg.manifest);
    // Native passes NO `refresh` → stop (never touch the unreliable patched fetch).
    // Web passes `refresh: true` → augment with the full served manifest, keeping the
    // bundle as the safety net.
    if (!cfg.refresh) return;
  }

  // Same-origin browser fetch (no CapacitorHttp). Reached with no bundled manifest
  // (native CDN / dev) OR on a web refresh; on failure we keep the bundled fallback.
  const base = _prebakedBase;
  void (async () => {
    try {
      const res = await fetch(`${base}/manifest.json`, { cache: 'no-store' });
      if (!res.ok) return;
      const m = await res.json() as PrebakedManifest;
      if (_prebakedBase !== base) return; // a newer configurePrebakedAudio() superseded us
      applyManifest(m);
    } catch { /* keep the bundled fallback (or the other tiers) */ }
  })();
}

function prebakedAvailable(): boolean { return !!_prebakedKeys && _prebakedKeys.size > 0; }

/** Number of pre-generated (offline HD) clips available for the arcade games. 0
 *  means games have no offline HD floor (native → robotic OS voice). */
export function prebakedKeyCount(): number { return _prebakedKeys ? _prebakedKeys.size : 0; }

function prebakedUrlFor(rawText: string): string | null {
  if (!_prebakedKeys || !_prebakedBase) return null;
  const clean = rawText.replace(EMOJI_RE, '').trim();
  // French session: FR-voice key first (native-FR clip), then the default key.
  if (_prebakedFrVoice && _langCode.toLowerCase().startsWith('fr')) {
    const frKey = ttsAudioKey(clean, _prebakedFrVoice);
    if (_prebakedKeys.has(frKey)) return `${_prebakedBase}/${frKey}.${_prebakedFormat}`;
  }
  const key = ttsAudioKey(clean, _prebakedVoice);
  return _prebakedKeys.has(key) ? `${_prebakedBase}/${key}.${_prebakedFormat}` : null;
}

/**
 * True when `text` has a prebaked HD clip available RIGHT NOW (bundle/manifest
 * loaded, FR-first keying in a FR session — the same ladder playback uses).
 * Callers with OPTIONAL flourish lines (the arcade "Find the word!" cue, a
 * word-in-context example sentence) gate on this so an un-rendered line is
 * SKIPPED, never spoken robotically (silence-over-robotic, CLAUDE.md audio #1).
 * Returns false before the manifest loads — optional lines simply stay quiet
 * for that first beat rather than risking the OS voice.
 */
export function hasPrebakedClip(text: string): boolean {
  return prebakedUrlFor(text) !== null;
}

async function startPrebakedAudio(src: string, onEnd?: () => void, revoke?: () => void): Promise<boolean> {
  const audio = new Audio(src) as TrackedAudio;
  applyVolume(audio);
  applyEmphasis(audio); // slow sound-out → slower override (prebaked ignores the rate param)
  if (revoke) audio._revoke = revoke;
  _prebakedAudio = audio;
  const done = () => { audio._revoke?.(); if (_prebakedAudio === audio) _prebakedAudio = null; onEnd?.(); };
  audio.onended = done;
  audio.onerror = done;
  try {
    await audio.play();
    return true;
  } catch {
    audio._revoke?.();
    if (_prebakedAudio === audio) _prebakedAudio = null;
    return false;
  }
}

async function playPrebaked(rawText: string, onEnd?: () => void, seq?: number): Promise<boolean> {
  // Belt-and-braces iOS mic crash guard: never start audio over a live record session.
  if (isCapacitorNative() && isMicRecordingLive()) { onEnd?.(); return true; }
  const url = prebakedUrlFor(rawText);
  if (!url) return false;
  if (seq !== undefined && seq !== _speakSeq) return true;
  // Primary: play the bundled clip directly via <audio src>.
  if (await startPrebakedAudio(url, onEnd)) return true;
  if (seq !== undefined && seq !== _speakSeq) return true;
  // Native fallback: WKWebView can fail to load a bundled same-origin clip via
  // <audio src> (byte-range under the capacitor local server), dropping arcade
  // audio to the robotic OS voice. Re-read the SAME file as a binary-safe blob and
  // play that blob: URL — the path the cloud/device-cache tiers use on native.
  // No-op on web (the primary already succeeded).
  if (isCapacitorNative()) {
    const blob = await fetchLocalAudio(url);
    if (seq !== undefined && seq !== _speakSeq) return true;
    if (blob) {
      const objUrl = URL.createObjectURL(blob);
      let revoked = false;
      const revoke = () => { if (!revoked) { revoked = true; URL.revokeObjectURL(objUrl); } };
      if (await startPrebakedAudio(objUrl, onEnd, revoke)) return true;
    }
  }
  return false;
}

// ── Pronunciation normalisation ──────────────────────────────────────────────
// Cloud TTS engines (Azure Neural, browser OS) mispronounce short isolated words
// because they lack sentence context. Two fixes applied before every TTS call:
// 1. Known-bad words → phonetic spellings that the engine reads correctly.
// 2. Short isolated words (≤6 chars, no spaces) get a trailing period so the
//    engine sees a sentence boundary and doesn't apply proper-name heuristics
//    (e.g. "cat" → "Kate" without this, because Azure reads "Cat" as a name).

const PRONUNCIATION_OVERRIDES_EN: Record<string, string> = {
  // NOTE: 'the' → 'thuh' was removed to match web-child (see its overrides
  // table): the respelling made neural voices read a hard onset — testers
  // heard "the" as a loud "DA". Never re-add /ð/ respellings here.
  // 'a'→'uh' REMOVED 2026-08-01: injected 'uh' → DragonHD foreign-accent drift on
  // article sentences. KEEP IN SYNC with generate-tts-audio.mjs + web-child.
  // 'pure'/'cure' are French words too → multilingual cloud voice drifts to French
  // (/pyʁ/ = "puurreeettt"); English-anchor them (Pure Shore "ure" island). Mirrors
  // web-child + generate-tts-audio.mjs.
  'pure': 'pyoor', 'cure': 'kyoor',
  // 'fine'/'wine' overrides REMOVED 2026-08-03: the 'fyne'/'wyne' respellings render
  // WRONG in DragonHD ('wyne' -> 'vineaa', 'fyne' -> 'inaa'); raw words voice correctly.
  // Mirrors web-child + generator.
  // Standalone 'ine' phonogram cue ("ine, as in fine") renders raw as "ee-neh"; respell to
  // 'ighn' → /aɪn/ (the vowel in mine/fine, like "sign"). Word-boundary match affects ONLY
  // the lone "ine" cue, never fine/wine/mine/vine/nine/shine. Mirrors web-child + generator.
  'ine': 'ighn',
};
const PRONUNCIATION_OVERRIDES_FR: Record<string, string> = {};

// English letter NAMES — a bare single letter handed to the multilingual cloud
// voice language-drifts to a French/Italian letter name (h→"ashhh", v→"vue",
// m→"misure" — the arcade bug). Respelling to an English name anchors the language.
// KEEP IN SYNC with web-child + the generator's LETTER_NAMES_EN.
// a:'ay.'→'ay' (2026-08-17): owner ear-check on 'ay.' FAILED — it drawled as
// "aayyee" (the trailing period forced a long sentence-final vowel). Dropped the
// period for a crisp letter-name "ay" /eɪ/ (verified jay/kay -ay grapheme). Keys are
// unchanged (raw char). KEEP IN SYNC with web-child + the generator's LETTER_NAMES_EN.
const LETTER_NAMES_EN: Record<string, string> = {
  a: 'ay', b: 'bee', c: 'see', d: 'dee', e: 'ee', f: 'eff', g: 'gee', h: 'aitch', i: 'eye',
  j: 'jay', k: 'kay', l: 'ell', m: 'emm', n: 'en', o: 'oh', p: 'pee', q: 'cue', r: 'are',
  s: 'ess', t: 'tee', u: 'you', v: 'vee', w: 'double you', x: 'ex', y: 'why', z: 'zee',
};

function applyPronunciationFixes(text: string): string {
  const isFr = _langCode.startsWith('fr');
  // A standalone single letter (letter tile / A–Z). In EN, respell to its English
  // NAME so the cloud voice can't drift to a French letter name; FR sessions and
  // non-letters keep the raw char. Keys are unchanged (lookup is on the raw char).
  const t = text.trim();
  if (t.length === 1) {
    const name = LETTER_NAMES_EN[t.toLowerCase()];
    return name && !isFr ? name : text;
  }
  const overrides = isFr ? PRONUNCIATION_OVERRIDES_FR : PRONUNCIATION_OVERRIDES_EN;
  let result = text;
  for (const [word, replacement] of Object.entries(overrides)) {
    // 13d (linguistic audit): 'a'→'uh' is the schwa'd ARTICLE ("a cat") — never the
    // vowel-sound/name "a" in a list like "a, e, i, o, u". Scope it to article position
    // (a + space + a 2+ letter word). KEEP IN SYNC with web-child + generate-tts-audio.mjs.
    const re = word === 'a'
      ? /\ba\b(?=\s+[A-Za-zÀ-ÿ]{2,})/gi
      : new RegExp(`\\b${word}\\b`, 'gi');
    result = result.replace(re, (match) =>
      match[0] === match[0].toUpperCase()
        ? replacement.charAt(0).toUpperCase() + replacement.slice(1)
        : replacement
    );
  }
  // Single isolated short word — append a period so cloud TTS treats it as a
  // sentence-final token rather than a truncated proper noun.
  if (!result.includes(' ') && result.length <= 6 && !/[.!?,;]$/.test(result)) {
    result = result + '.';
  }
  return result;
}

// ── Device audio cache tier (shared store with lessons) ──────────────────────
let _deviceCacheAudio: TrackedAudio | null = null;
function deviceCacheAvailable(): boolean { return audioCacheCount() > 0; }
function cloudTextFor(rawText: string): string {
  return applyPronunciationFixes(rawText.replace(EMOJI_RE, '').trim());
}
function deviceCacheKeyFor(rawText: string): string | null {
  if (!_cloudTts) return null;
  const text = cloudTextFor(rawText);
  if (!text) return null;
  return ttsAudioKey(text, resolvedCloudVoice());
}

async function playDeviceCache(rawText: string, onEnd?: () => void, seq?: number): Promise<boolean> {
  // Belt-and-braces iOS mic crash guard: never start audio over a live record session.
  if (isCapacitorNative() && isMicRecordingLive()) { onEnd?.(); return true; }
  const key = deviceCacheKeyFor(rawText);
  if (!key || !audioCacheHas(key)) return false;
  const blob = await getAudioBlob(key);
  if (!blob) return false;
  if (seq !== undefined && seq !== _speakSeq) return true;
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url) as TrackedAudio;
  applyVolume(audio);
  applyEmphasis(audio); // slow sound-out → slower override
  let revoked = false;
  audio._revoke = () => { if (!revoked) { revoked = true; URL.revokeObjectURL(url); } };
  _deviceCacheAudio = audio;
  const done = () => { audio._revoke?.(); if (_deviceCacheAudio === audio) _deviceCacheAudio = null; onEnd?.(); };
  audio.onended = done;
  audio.onerror = done;
  try {
    await audio.play();
  } catch {
    audio._revoke?.();
    if (_deviceCacheAudio === audio) _deviceCacheAudio = null;
    return false;
  }
  return true;
}

// ── Pack-download tier (Phase 1 iOS app-size fix — mirrors web-child) ─────────
// The native app bundles only a small FLOOR of clips; every other corpus line
// (game vocab included) is DOWNLOADED on first play as its exact prebaked HD clip
// from the CDN (binary-safe CapacitorHttp GET), cached on-device, and played via
// the same blob path as the device-cache tier — so un-bundled arcade words are HD,
// never robotic. No-op on web (the web image ships the full corpus).
let _packBase: string | null = null;
let _packKeys: Set<string> | null = null;
let _packFormat = 'mp3';
let _packAudio: TrackedAudio | null = null;

export function configurePackDownload(cfg: { baseUrl: string; keys: string[]; format?: string } | null): void {
  if (!cfg || !cfg.baseUrl || !Array.isArray(cfg.keys) || cfg.keys.length === 0) {
    _packBase = null;
    _packKeys = null;
    return;
  }
  _packBase = cfg.baseUrl.replace(/\/$/, '');
  _packKeys = new Set(cfg.keys);
  _packFormat = cfg.format ?? 'mp3';
}

/** Number of downloadable pack keys currently configured (diagnostics/tests). */
export function packKeyCount(): number { return _packKeys ? _packKeys.size : 0; }

/* Race-safety for a DEFERRED pack manifest (P1 boot-perf fix 2026-08 — mirrors
 * web-child): mobile loads its ~8.8 MB contentPackManifest via a BUNDLED dynamic
 * import() off the boot path, then calls configurePackDownload when it lands. A
 * game line spoken in that window must not mis-skip the pack tier (it would hit
 * the native NEVER-ROBOTIC hard-gate and go silent even though its HD clip is
 * downloadable), so the speak chain AWAITS this promise before the pack tier. */
let _packConfigPending: Promise<void> | null = null;

/** Register the in-flight pack-manifest load (null clears). The tiered speak path
 *  awaits it before the pack-download tier / native hard-gate decision. */
export function setPackDownloadPending(p: Promise<unknown> | null): void {
  if (!p) { _packConfigPending = null; return; }
  const settled: Promise<void> = p.then(() => undefined, () => undefined);
  _packConfigPending = settled;
  void settled.then(() => { if (_packConfigPending === settled) _packConfigPending = null; });
}

/** Await any in-flight pack-manifest load (no-op when none). */
async function awaitPackConfig(): Promise<void> {
  if (_packConfigPending) await _packConfigPending;
}

/** True when download-on-miss is configured → the tier gate must route through the
 *  tiered path (else an un-bundled line would skip straight to the OS voice). */
function packDownloadAvailable(): boolean { return !!_packBase && !!_packKeys && _packKeys.size > 0; }

function isOnline(): boolean {
  try {
    const nav = (typeof window !== 'undefined' && window.navigator) ? window.navigator
      : (typeof navigator !== 'undefined' ? navigator : undefined);
    return !nav || nav.onLine !== false;
  } catch { return true; }
}

function packKeyFor(rawText: string): string | null {
  if (!_packKeys) return null;
  const clean = rawText.replace(EMOJI_RE, '').trim();
  if (_prebakedFrVoice && _langCode.toLowerCase().startsWith('fr')) {
    const frKey = ttsAudioKey(clean, _prebakedFrVoice);
    if (_packKeys.has(frKey)) return frKey;
  }
  if (_prebakedVoice) {
    const k = ttsAudioKey(clean, _prebakedVoice);
    if (_packKeys.has(k)) return k;
  }
  return null;
}

async function playPackDownload(rawText: string, onEnd?: () => void, seq?: number): Promise<boolean> {
  if (isCapacitorNative() && isMicRecordingLive()) { onEnd?.(); return true; }
  if (!_packBase || !_packKeys) return false;
  const key = packKeyFor(rawText);
  if (!key) return false;                 // not a known pack line → let the cloud tier handle it
  let blob: Blob | null = audioCacheHas(key) ? await getAudioBlob(key) : null;
  if (!blob) {
    if (!isCapacitorNative()) return false;
    if (!isOnline()) {
      console.warn(`[tts] pack line not yet downloaded and device is OFFLINE (key ${key}); using on-device neural/OS until reconnected.`);
      return false;
    }
    blob = await fetchLocalAudio(`${_packBase}/${key}.${_packFormat}`);
    if (blob) void putAudioBlob(key, blob);
  }
  if (!blob) return false;
  if (seq !== undefined && seq !== _speakSeq) return true; // superseded
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url) as TrackedAudio;
  applyVolume(audio);
  applyEmphasis(audio); // slow sound-out → slower override
  let revoked = false;
  audio._revoke = () => { if (!revoked) { revoked = true; URL.revokeObjectURL(url); } };
  _packAudio = audio;
  const done = () => { audio._revoke?.(); if (_packAudio === audio) _packAudio = null; onEnd?.(); };
  audio.onended = done;
  audio.onerror = done;
  try {
    await audio.play();
  } catch {
    audio._revoke?.();
    if (_packAudio === audio) _packAudio = null;
    return false;
  }
  return true;
}

function stopStreamAudio(): void {
  if (_prebakedAudio) { try { _prebakedAudio.pause(); } catch { /* ignore */ } _prebakedAudio = null; }
  if (_packAudio) { try { _packAudio.pause(); } catch { /* ignore */ } _packAudio._revoke?.(); _packAudio = null; }
  if (_deviceCacheAudio) { try { _deviceCacheAudio.pause(); } catch { /* ignore */ } _deviceCacheAudio._revoke?.(); _deviceCacheAudio = null; }
  if (_cloudAudio) { try { _cloudAudio.pause(); } catch { /* ignore */ } _cloudAudio._revoke?.(); _cloudAudio = null; }
  if (_neuralAudio) { try { _neuralAudio.pause(); } catch { /* ignore */ } _neuralAudio._revoke?.(); _neuralAudio = null; }
}

let _speakSeq = 0;

/** Speak through the best tier: cloud → on-device neural → OS voice. */
function speakTiered(
  text: string,
  rate: number,
  onEnd: (() => void) | undefined,
  localFn: (t: string, r: number, e?: () => void) => void,
  allowCloudFallback = false,
): void {
  const seq = ++_speakSeq;
  stopStreamAudio();
  const guarded = onEnd ? () => { if (seq === _speakSeq) onEnd(); } : undefined;
  void (async () => {
    // Best: a pre-generated file for this exact line (instant, offline, no auth).
    if (await playPrebaked(text, guarded, seq)) return;
    if (seq !== _speakSeq) return;
    // Next: an on-device-cached clip from a previous cloud synth — HD, offline.
    if (await playDeviceCache(text, guarded, seq)) return;
    if (seq !== _speakSeq) return;
    // Deferred pack manifest (mobile boot-perf): if the bundled manifest is still
    // loading, WAIT for it — otherwise this line would skip the pack tier and hit
    // the native hard-gate below even though its HD clip is one download away.
    await awaitPackConfig();
    if (seq !== _speakSeq) return;
    // Download-on-miss: un-bundled corpus line → fetch its exact prebaked HD clip
    // from the CDN, cache it, and play (native). No-op on web.
    if (await playPackDownload(text, guarded, seq)) return;
    if (seq !== _speakSeq) return;
    // NEVER-ROBOTIC HARD-GATE (native) — parity with web-child speechService. The
    // arcade/phonogram/wordblaze games speak through THIS instance, which had ZERO
    // corpus protection (the 2026-07-24 "robotic in games" report). On native, cloud
    // (CapacitorHttp) + OS voice are robotic, so if no on-device tier served the line
    // we STOP rather than fall through. Only an explicit allowCloudFallback caller
    // (the tutor's dynamic LLM replies) may reach the cloud. Fire onEnd so gated
    // progression doesn't hang on the silence.
    if (isCapacitorNative() && !allowCloudFallback) {
      console.error('[tts] native corpus line had no on-device HD clip — HARD-STOP (no cloud/OS fallback).');
      try { const w = globalThis as unknown as { __lwaBlockedCorpus?: number }; w.__lwaBlockedCorpus = (w.__lwaBlockedCorpus ?? 0) + 1; } catch { /* ignore */ }
      guarded?.();
      return;
    }
    if (cloudAvailable() && await playCloud(text, rate, guarded, seq)) return;
    if (seq !== _speakSeq) return;
    if (neuralAvailable() && await playNeural(text, rate, guarded, seq)) return;
    if (seq !== _speakSeq) return;
    localFn(text, rate, onEnd);
  })();
}


// iOS mic crash guard (SHARED state from ui-shared — one flag with the child
// app + the native shim). Math/arcade screens have no mic of their own, but a
// lingering recognizer across navigation can still hold the record session —
// and this service instance previously had ZERO defense. Drop while live,
// defer through the release cooldown.
function micGuard(retry: () => void, onEnd?: () => void): boolean {
  if (!isCapacitorNative()) return false;
  if (isMicRecordingLive()) { onEnd?.(); return true; }
  const wait = msUntilMicReleased();
  if (wait > 0) { window.setTimeout(retry, wait + 120); return true; }
  return false;
}

export function speak(text: string, rate = 0.91, onEnd?: () => void, allowCloudFallback = false): void {
  if (micGuard(() => speak(text, rate, onEnd, allowCloudFallback), onEnd)) return; // iOS mic crash guard
  _emphasis = null; // ordinary speech is never slowed (only the slow sound-out replays are)
  _emitSpeech('start');
  const endFn = () => { _emitSpeech('end'); onEnd?.(); };
  if (prebakedAvailable() || deviceCacheAvailable() || packDownloadAvailable() || cloudAvailable() || neuralAvailable()) {
    speakTiered(text, rate, endFn, speakLocal, allowCloudFallback);
    return;
  }
  speakLocal(text, rate, endFn);
}

function speakLocal(text: string, rate = 0.91, onEnd?: () => void): void {
  if (isCapacitorNative() && getNativeTextToSpeech()) { speakNative(text, rate, onEnd); return; }
  if (!('speechSynthesis' in window)) { speakNative(text, rate, onEnd); return; }
  window.speechSynthesis.cancel();
  startKeepAlive();
  const clean = applyPronunciationFixes(text.replace(EMOJI_RE, '').trim());
  if (!clean) { onEnd?.(); return; }
  const chunks = chunkText(clean);
  let cached: CachedVoice;

  const speakChunk = (i: number) => {
    const utter = new window.SpeechSynthesisUtterance(chunks[i]);
    utter.lang = _langCode;
    utter.rate = cached.rate ?? rate;
    utter.pitch = cached.pitch ?? 1.0;
    utter.volume = SPEECH_VOLUME; // same target as the HD <audio> tiers — never louder on fallback
    if (cached.voice) utter.voice = cached.voice;
    const isLast = i === chunks.length - 1;
    // Chrome sometimes never fires onend for short utterances — use a fallback
    // timer so lessons always advance and never freeze waiting for a callback.
    const chunkMs = Math.max(1000, (chunks[i].length / 10) * 1000 / (cached.rate ?? rate));
    const fallbackId = setTimeout(() => {
      if (isLast) { stopKeepAlive(); onEnd?.(); } else speakChunk(i + 1);
    }, chunkMs + 2000);
    utter.onend = () => { clearTimeout(fallbackId); if (isLast) { stopKeepAlive(); onEnd?.(); } else speakChunk(i + 1); };
    utter.onerror = (e: any) => {
      clearTimeout(fallbackId);
      if (e?.error === 'interrupted') return;
      if (isLast) { stopKeepAlive(); onEnd?.(); } else speakChunk(i + 1);
    };
    window.speechSynthesis.speak(utter);
  };

  // P3 (accent-impossible): resolve the voice only after the once-per-session
  // voiceschanged wait, and REFUSE a voice-less utterance — the engine default
  // for utter.lang can be a foreign accent. Refusal fires onEnd immediately.
  const begin = () => {
    cached = ensureCache();
    if (!cached.voice) {
      stopKeepAlive();
      refuseOsSpeech('the browser voice list has no acceptable voice for the session language');
      onEnd?.();
      return;
    }
    // Chrome workaround: brief delay after cancel()
    setTimeout(() => speakChunk(0), 50);
  };
  if (browserVoicesSettled()) begin();
  else void ensureBrowserVoicesReady().then(begin);
}

/** Speak at a custom rate (overrides the voice's default rate). */
export function speakAtRate(text: string, rate: number, onEnd?: () => void): void {
  if (micGuard(() => speakAtRate(text, rate, onEnd), onEnd)) return; // iOS mic crash guard
  _emphasis = null; // ordinary speech is never slowed (only the slow sound-out replays are)
  _emitSpeech('start');
  const endFn = () => { _emitSpeech('end'); onEnd?.(); };
  if (prebakedAvailable() || deviceCacheAvailable() || packDownloadAvailable() || cloudAvailable() || neuralAvailable()) {
    speakTiered(text, rate, endFn, speakAtRateLocal);
    return;
  }
  speakAtRateLocal(text, rate, endFn);
}

/** Replay a word SLOWER (and at full volume) — the repeat-tap "I missed it"
 *  path. Delegates to speakEmphasizedAtRate. Mirrors web-child. */
export function speakEmphasized(text: string, intensity = 1, onEnd?: () => void): void {
  // iOS mic crash guard — replays (the 🔊 repeat-tap path) were UNGUARDED and
  // could start audio over a live record session: the exact full-crash class.
  // (speakEmphasizedAtRate guards again — belt-and-braces, deferral-safe.)
  if (micGuard(() => speakEmphasized(text, intensity, onEnd), onEnd)) return;
  const rate = Math.max(0.55, 0.85 - 0.12 * Math.max(1, intensity));
  speakEmphasizedAtRate(text, rate, onEnd);
}

/**
 * Speak at an EXACT slower rate with the slow-down applied to the HD clip
 * itself. speakAtRate's `rate` param only reaches the OS-utterance fallback —
 * prebaked / pack / device-cache / cloud clips ignore it (see the _emphasis note
 * above) — so the WordCorrection "sound it out slower" second read used to play
 * at NORMAL speed wherever the HD floor served it (this was fine on web-child,
 * broken here on web-app). This entry point routes the exact rate through the
 * emphasis mechanism (<audio>.playbackRate, pitch preserved, full correction
 * volume — the documented missed-word exception), so the syllable sound-out is
 * REALLY slower on every tier. Rate clamps to [0.5, 1]: below ~0.5 WKWebView's
 * pitch-preserving playbackRate gets glitchy/muted. Mirrors web-child.
 */
export function speakEmphasizedAtRate(text: string, rate: number, onEnd?: () => void): void {
  // iOS mic crash guard — replays (the 🔊 repeat-tap path) were UNGUARDED and
  // could start audio over a live record session: the exact full-crash class.
  if (micGuard(() => speakEmphasizedAtRate(text, rate, onEnd), onEnd)) return;
  const r = Math.min(1, Math.max(0.5, rate));
  _emphasis = { rate: r };
  _emitSpeech('start');
  const endFn = () => { _emphasis = null; _emitSpeech('end'); onEnd?.(); };
  if (prebakedAvailable() || deviceCacheAvailable() || packDownloadAvailable() || cloudAvailable() || neuralAvailable()) {
    speakTiered(text, r, endFn, speakAtRateLocal);
    return;
  }
  speakAtRateLocal(text, r, endFn);
}

function speakAtRateLocal(text: string, rate: number, onEnd?: () => void): void {
  if (isCapacitorNative() && getNativeTextToSpeech()) { speakNative(text, rate, onEnd); return; }
  if (!('speechSynthesis' in window)) { speakNative(text, rate, onEnd); return; }
  window.speechSynthesis.cancel();
  startKeepAlive();
  const clean = applyPronunciationFixes(text.replace(EMOJI_RE, '').trim());
  if (!clean) { onEnd?.(); return; }
  const chunks = chunkText(clean);
  let cached: CachedVoice;

  const speakChunk = (i: number) => {
    const utter = new window.SpeechSynthesisUtterance(chunks[i]);
    utter.lang = _langCode;
    utter.rate = rate;
    utter.pitch = cached.pitch ?? 1.0;
    utter.volume = SPEECH_VOLUME; // same target as the HD <audio> tiers — never louder on fallback
    if (cached.voice) utter.voice = cached.voice;
    const isLast = i === chunks.length - 1;
    const chunkMs = Math.max(1000, (chunks[i].length / 10) * 1000 / rate);
    const fallbackId = setTimeout(() => {
      if (isLast) { stopKeepAlive(); onEnd?.(); } else speakChunk(i + 1);
    }, chunkMs + 2000);
    utter.onend = () => { clearTimeout(fallbackId); if (isLast) { stopKeepAlive(); onEnd?.(); } else speakChunk(i + 1); };
    utter.onerror = (e: any) => {
      clearTimeout(fallbackId);
      if (e?.error === 'interrupted') return;
      if (isLast) { stopKeepAlive(); onEnd?.(); } else speakChunk(i + 1);
    };
    window.speechSynthesis.speak(utter);
  };

  // P3 (accent-impossible): same voiceschanged wait + voice-less refusal as speakLocal.
  const begin = () => {
    cached = ensureCache();
    if (!cached.voice) {
      stopKeepAlive();
      refuseOsSpeech('the browser voice list has no acceptable voice for the session language');
      onEnd?.();
      return;
    }
    setTimeout(() => speakChunk(0), 50);
  };
  if (browserVoicesSettled()) begin();
  else void ensureBrowserVoicesReady().then(begin);
}

// Gap between the letters of a slow sound-out (ms). Long enough that a child
// hears each part as separate, short enough to keep the arcade moving.
const SOUND_OUT_GAP_MS = 220;
// Emphasis intensity for the sound-out → rate ≈ 0.61 (see speakEmphasized).
const SOUND_OUT_INTENSITY = 2;

/**
 * SLOW, letter-by-letter SOUND-OUT of a word — the arcade "sound it out" coach,
 * built to the SAME model the lessons use (web-child WordBreakdown): speak each
 * part slowly and clearly. It uses ONLY already-prebaked HD clips — the whole
 * word is spoken by the caller, and every single letter a–z is baked — so it
 * mints NO new spoken literal (audio rule #10) and can never go robotic.
 *
 * Why speakEmphasized and not speakAtRate: the prebaked tier IGNORES speakAtRate's
 * rate; only the emphasis mechanism (<audio>.playbackRate, pitch preserved)
 * actually slows a served HD clip (see speakEmphasizedAtRate). So this is the
 * only way the slow-down really reaches the child on the offline floor.
 *
 * Self-cancelling: each letter is chained off the previous clip's guarded onEnd
 * (speakTiered's _speakSeq guard) and the inter-letter gap re-checks _speakSeq,
 * so ANY newer speak()/cancelSpeech() — the child catching the next letter, a new
 * word, a pause or quit — aborts the remaining chain. The two never overlap.
 */
export function soundOutWord(word: string, onDone?: () => void): void {
  const letters = (word ?? '').trim().toLowerCase().replace(/[^a-zà-ÿ]/gi, '').split('');
  if (!letters.length) { onDone?.(); return; }
  const step = (i: number) => {
    if (i >= letters.length) { onDone?.(); return; }
    speakEmphasized(letters[i], SOUND_OUT_INTENSITY, () => {
      // onEnd only fires if this clip wasn't superseded. Capture the current
      // sequence; if a newer speak()/cancel bumps it during the gap, abort.
      const seq = _speakSeq;
      setTimeout(() => { if (seq === _speakSeq) step(i + 1); }, SOUND_OUT_GAP_MS);
    });
  };
  step(0);
}

// Speech event listeners — used by BackgroundMusicProvider for TTS ducking.
type SpeechEventType = 'start' | 'end';
type SpeechEventListener = (event: SpeechEventType) => void;
let _speechListeners: SpeechEventListener[] = [];

export function onSpeechEvent(fn: SpeechEventListener): () => void {
  _speechListeners.push(fn);
  return () => { _speechListeners = _speechListeners.filter(f => f !== fn); };
}

function _emitSpeech(event: SpeechEventType): void {
  for (const fn of _speechListeners) fn(event);
}

export function cancelSpeech(): void {
  _emitSpeech('end');
  _speakSeq++;                       // invalidate any in-flight tiered speak
  stopKeepAlive();
  stopStreamAudio();
  void getNativeTextToSpeech()?.stop?.().catch(() => { /* ignore */ });
  if ('speechSynthesis' in window) window.speechSynthesis.cancel();
}

export function isSpeaking(): boolean {
  return window.speechSynthesis?.speaking ?? false;
}

/** Set current language — call from any page on mount. (Native voice index is
 *  resolved fresh per utterance — H1 — so only the browser cache clears here.) */
export function setVoiceLang(langCode: string): void {
  if (langCode !== _langCode) {
    _langCode = langCode;
    _cache = null;
  }
}

/** No-op stub — web-app does not have the IndexedDB audio cache layer. */
export async function prefetchAudio(_rawLines: string[]): Promise<{ total: number; alreadyHave: number; fetched: number; failed: number }> {
  return { total: 0, alreadyHave: 0, fetched: 0, failed: 0 };
}
